import { useParams, Link, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { Calendar, Clock, ArrowRight, ChevronLeft, ChevronDown, Loader2 } from "lucide-react";
import SEO from "../seo/SEO";
import { ORGANIZATION_SCHEMA, buildBreadcrumbSchema, buildFAQSchema, SITE_DOMAIN } from "../seo/seoConfig";
import { BLOG_POSTS } from "../seo/blogPostsConfig";

const heading = { fontFamily: "'Satoshi', sans-serif" };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const staticPost = BLOG_POSTS.find(p => p.slug === slug);

  // For non-static slugs, attempt to fetch from blog API
  const [dynPost, setDynPost] = useState(null);
  const [dynLoading, setDynLoading] = useState(!staticPost);
  const [dynError, setDynError] = useState(false);

  useEffect(() => {
    if (staticPost) return;
    let cancelled = false;
    setDynLoading(true);
    axios.get(`${API}/blog/post/${slug}`)
      .then((res) => { if (!cancelled) { setDynPost(res.data); setDynLoading(false); } })
      .catch(() => { if (!cancelled) { setDynError(true); setDynLoading(false); } });
    return () => { cancelled = true; };
  }, [slug, staticPost]);

  if (!staticPost && dynLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }
  if (!staticPost && (dynError || !dynPost)) return <Navigate to="/blog" replace />;
  if (!staticPost && dynPost) {
    return <DynamicBlogPostView post={dynPost} />;
  }
  const post = staticPost;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    datePublished: post.published,
    dateModified: post.updated,
    author: { "@type": "Organization", name: post.author || "InvoiceSetu" },
    publisher: {
      "@type": "Organization",
      name: "InvoiceSetu",
      logo: { "@type": "ImageObject", url: `${SITE_DOMAIN}/assets/logo.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_DOMAIN}/blog/${post.slug}` },
  };

  return (
    <div className="bg-white min-h-screen" data-testid={`blog-post-${post.slug}`}>
      <SEO
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        ogType="article"
        schemas={[
          ORGANIZATION_SCHEMA,
          articleSchema,
          buildBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Blog", url: "/blog" },
            { name: post.h1, url: `/blog/${post.slug}` },
          ]),
          buildFAQSchema(post.faqs || []),
        ]}
      />

      <div className="bg-white/90 backdrop-blur border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
            <ChevronLeft className="w-4 h-4" /> All articles
          </Link>
          <Link to="/auth" className="bg-blue-600 text-white font-semibold rounded-lg px-4 py-1.5 hover:bg-blue-700 text-xs sm:text-sm">Start Free</Link>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-600">{post.category}</span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 mt-2 mb-4 leading-[1.1]" style={heading}>{post.h1}</h1>
        <div className="flex items-center gap-4 text-xs text-slate-500 mb-6 pb-6 border-b border-slate-100">
          <span>By {post.author}</span>
          <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {post.published}</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readMin} min read</span>
        </div>

        {post.hero && (
          <p className="text-lg text-slate-600 italic mb-8 leading-relaxed">{post.hero}</p>
        )}

        {/* Body sections */}
        <div className="prose-content space-y-4 text-slate-700 text-base leading-relaxed">
          {post.sections.map((s, i) => <SectionRenderer key={i} section={s} />)}
        </div>

        {/* Mid-article CTA */}
        <div className="my-10 p-6 bg-blue-50 border border-blue-100 rounded-2xl">
          <h3 className="font-black text-slate-900 mb-1.5" style={heading}>Try it without paperwork</h3>
          <p className="text-sm text-slate-600 mb-3">Generate professional GST invoices in 30 seconds — free forever, no credit card.</p>
          <Link to="/auth" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-800">
            Start Free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* FAQs */}
        {post.faqs && post.faqs.length > 0 && (
          <div className="mt-10">
            <h2 className="text-2xl font-black text-slate-900 mb-4" style={heading}>FAQs</h2>
            <div className="space-y-2">
              {post.faqs.map((f, i) => <Faq key={i} q={f.q} a={f.a} index={i} />)}
            </div>
          </div>
        )}

        {/* Related posts */}
        {post.related && post.related.length > 0 && (
          <div className="mt-12 pt-10 border-t border-slate-100">
            <h2 className="text-xl font-black text-slate-900 mb-4" style={heading}>Keep reading</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {post.related
                .map(slug => BLOG_POSTS.find(p => p.slug === slug))
                .filter(Boolean)
                .map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    className="bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-300 transition-colors"
                  >
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-blue-600">{p.category}</span>
                    <p className="font-semibold text-slate-900 mt-1">{p.title}</p>
                  </Link>
                ))}
            </div>
          </div>
        )}
      </article>

      <footer className="border-t border-slate-100 py-8 px-4 text-center text-xs text-slate-400">
        Powered by <Link to="/" className="text-blue-600 font-semibold">InvoiceSetu</Link>
      </footer>
    </div>
  );
}

function SectionRenderer({ section }) {
  switch (section.kind) {
    case "p":
      return <p className="leading-relaxed">{section.text}</p>;
    case "h2":
      return <h2 className="text-2xl font-black text-slate-900 mt-10 mb-3" style={heading}>{section.text}</h2>;
    case "h3":
      return <h3 className="text-lg font-bold text-slate-900 mt-6 mb-2" style={heading}>{section.text}</h3>;
    case "ul":
      return (
        <ul className="space-y-1.5 list-disc pl-5 text-slate-700">
          {section.items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      );
    case "ol":
      return (
        <ol className="space-y-2 list-decimal pl-5 text-slate-700">
          {section.items.map((it, i) => <li key={i}>{it}</li>)}
        </ol>
      );
    case "callout": {
      const tone = section.tone === "amber"
        ? "bg-amber-50 border-amber-200 text-amber-900"
        : "bg-blue-50 border-blue-200 text-blue-900";
      return (
        <div className={`my-4 p-4 rounded-xl border ${tone}`}>
          <p className="font-bold text-sm mb-1">{section.title}</p>
          <p className="text-sm leading-relaxed">{section.text}</p>
        </div>
      );
    }
    case "table":
      return (
        <div className="my-4 overflow-x-auto border border-slate-200 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {section.headers.map((h, i) => (
                  <th key={i} className="text-left px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  {r.map((c, j) => <td key={j} className="px-4 py-2.5 text-slate-700">{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "quote":
      return (
        <blockquote className="my-4 pl-4 border-l-4 border-blue-300 italic text-slate-700">
          {section.text}
        </blockquote>
      );
    default:
      return null;
  }
}

function Faq({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="bg-white rounded-xl border border-slate-100" data-testid={`blog-faq-${index}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-3"
      >
        <span className="font-semibold text-slate-900 text-sm sm:text-base">{q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{a}</div>}
    </div>
  );
}


/* ── Dynamic blog post (admin-published, Markdown body) ── */
function renderMarkdownLite(md) {
  if (!md) return null;
  const lines = md.split(/\r?\n/);
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      out.push(<ul key={`ul-${out.length}`} className="list-disc pl-6 my-3 space-y-1 text-slate-700">{listBuf.map((it, j) => <li key={j}>{it}</li>)}</ul>);
      listBuf = [];
    }
  };
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (/^###\s+/.test(line)) { flushList(); out.push(<h3 key={idx} className="text-lg font-bold text-slate-900 mt-5 mb-2" style={heading}>{line.replace(/^###\s+/, "")}</h3>); return; }
    if (/^##\s+/.test(line)) { flushList(); out.push(<h2 key={idx} className="text-2xl font-black text-slate-900 mt-7 mb-3" style={heading}>{line.replace(/^##\s+/, "")}</h2>); return; }
    if (/^#\s+/.test(line)) { flushList(); out.push(<h2 key={idx} className="text-2xl font-black text-slate-900 mt-7 mb-3" style={heading}>{line.replace(/^#\s+/, "")}</h2>); return; }
    if (/^[-*]\s+/.test(line)) { listBuf.push(line.replace(/^[-*]\s+/, "")); return; }
    if (line.trim() === "") { flushList(); return; }
    flushList();
    out.push(<p key={idx} className="text-base text-slate-700 leading-relaxed my-3">{line}</p>);
  });
  flushList();
  return out;
}

function DynamicBlogPostView({ post }) {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.created_at,
    dateModified: post.updated_at,
    author: { "@type": "Organization", name: post.author_name || "InvoiceSetu" },
    publisher: {
      "@type": "Organization",
      name: "InvoiceSetu",
      logo: { "@type": "ImageObject", url: `${SITE_DOMAIN}/assets/logo.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_DOMAIN}/blog/${post.slug}` },
  };
  return (
    <div className="bg-white min-h-screen" data-testid={`blog-post-${post.slug}`}>
      <SEO
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt}
        path={`/blog/${post.slug}`}
        ogType="article"
        schemas={[
          ORGANIZATION_SCHEMA,
          articleSchema,
          buildBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Blog", url: "/blog" },
            { name: post.title, url: `/blog/${post.slug}` },
          ]),
        ]}
      />
      <div className="bg-white/90 backdrop-blur border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
            <ChevronLeft className="w-4 h-4" /> All articles
          </Link>
          <Link to="/auth" className="bg-blue-600 text-white font-semibold rounded-lg px-4 py-1.5 hover:bg-blue-700 text-xs sm:text-sm">Start Free</Link>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-600">{post.category}</span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 leading-tight my-4" style={heading}>
          {post.title}
        </h1>
        <div className="flex items-center gap-4 text-xs text-slate-500 mb-7">
          <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {(post.created_at || "").slice(0, 10)}</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.read_minutes || 4} min read</span>
        </div>
        {post.cover_image_url && (
          <img src={post.cover_image_url} alt={post.title} className="w-full max-h-[420px] object-cover rounded-2xl mb-8" />
        )}
        <p className="text-lg text-slate-700 leading-relaxed mb-6 italic border-l-4 border-blue-200 pl-4">{post.excerpt}</p>
        <div data-testid="blog-dynamic-body">
          {renderMarkdownLite(post.body_markdown)}
        </div>
        <div className="mt-10 bg-blue-50 border border-blue-100 rounded-2xl p-6 text-center">
          <p className="text-sm text-slate-700 mb-3">Ready to apply this in your own business?</p>
          <Link to="/auth" className="inline-flex items-center gap-2 bg-blue-600 text-white font-semibold rounded-lg px-6 py-2.5 hover:bg-blue-700 text-sm">
            Try InvoiceSetu Free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </article>
    </div>
  );
}
